import React, { useState } from 'react';
import { Field } from 'formik';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import PropTypes from 'prop-types';
import Select from 'react-select';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import { CountryCodeSelectField, PhoneNumberInputField } from '../PhoneField';
import { passwordRequirements } from '../../../../shared/utils/registrationSchemaHelper';
import Text from '../../../../shared/_TBComponents/Text';

const ColumnContent = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  
  :first-of-type {
    margin-right: 30px;
  }

  @media screen and (max-width: 768px) {
    :first-of-type {
      margin-right: 20px;
    }
  }

  @media screen and (max-width: 425px) {
    :first-of-type {
      margin-right: 0;
      margin-bottom: 0;
    }
  }
`;

const ErrorAndName = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 5px;
  width: 100%;
`;

const FieldError = styled('div')`
  color: #ff6600;
  font-size: 12px;
`;

const FormColumns = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  
  label {
    margin-bottom: 5px;
  }

  @media screen and (max-width: 425px) {
    flex-direction: column;

    ${ColumnContent}:first-of-type {
      margin-bottom: 5px;
    }
  }
`;

const FormGroup = styled('div')`
  margin-bottom: 5px;
  z-index: 1;
  
  label {
    width: 100%;
  }
  
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

const genderOptions = { 1: { name: 'Male', id: 1 }, 2: { name: 'Female', id: 2 }, 3: { name: 'Other', id: 3 } };

const CommonFields = ({
  errors,
  setFieldValue,
  showGenderField,
  showOrgFields,
  showPasswordFields,
  touched,
  values,
}) => {
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const theme = useTheme();

  return (
    <>
      <FormGroup>
        <FormColumns>
          <ColumnContent>
            <label htmlFor='firstName'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  First Name
                </Text>
                {errors.firstName && touched.firstName && <FieldError className='text-center'>{errors.firstName}</FieldError>}
              </ErrorAndName>
              <Field
                className='form-control'
                id='firstName'
                maxLength={50}
                name='firstName'
                placeholder='First Name'
                type='text'
              />
            </label>
          </ColumnContent>
          <ColumnContent>
            <label htmlFor='lastName'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  Last Name
                </Text>
                {errors.lastName && touched.lastName && <FieldError className='text-center'>{errors.lastName}</FieldError>}
              </ErrorAndName>
              <Field
                className='form-control'
                id='lastName'
                maxLength={50}
                name='lastName'
                placeholder='Last Name'
                type='text'
              />
            </label>
          </ColumnContent>
        </FormColumns>
      </FormGroup>
      <FormGroup>
        <label htmlFor='email-field'>
          <ErrorAndName>
            <Text
              styledTextProps={{ fontSize: theme.textFontSizes[0] }}
            >
              Email Address
            </Text>
            {errors.emailAddress && touched.emailAddress && <FieldError className='text-center'>{errors.emailAddress}</FieldError>}
          </ErrorAndName>
          <Field
            className='form-control'
            id='email-field'
            maxLength={100}
            name='emailAddress'
            placeholder='Email'
            type='email'
          />
        </label>
      </FormGroup>
      {showPasswordFields ? (
        <>
          <FormGroup>
            <label htmlFor='password'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  Password
                </Text>
                {(errors.password && touched.password)
                  && errors.password !== passwordRequirements
                  && <FieldError>{errors.password}</FieldError>}
              </ErrorAndName>
              <PasswordFieldWrapper>
                <Field
                  className='form-control'
                  id='password'
                  name='password'
                  placeholder='Password'
                  type={showPassword ? 'text' : 'password'}
                />
                <PasswordToggleButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  type='button'
                >
                  {showPassword ? 'Hide Password' : 'Show Password'}
                  <span style={{ marginLeft: '8px' }}>
                    {showPassword ? <HiEyeOff /> : <HiEye />}
                  </span>
                </PasswordToggleButton>
              </PasswordFieldWrapper>
              <Text
                styledTextProps={(errors.password && touched.password) && errors.password === passwordRequirements ? { color: '#FF6600', fontSize: '12px', fontWeight: '400' } : { fontSize: '12px', fontWeight: '400' }}
                styledWrapperProps={{ margin: '4px 0px' }}
              >
                {passwordRequirements}
              </Text>
            </label>
          </FormGroup>
          <FormGroup>
            <label htmlFor='confirmedPassword'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  Re-enter Password
                </Text>
                {errors.confirmedPassword && touched.confirmedPassword && <FieldError className='text-center'>{errors.confirmedPassword}</FieldError>}
              </ErrorAndName>
              <PasswordFieldWrapper>
                <Field
                  className='form-control'
                  id='confirmedPassword'
                  name='confirmedPassword'
                  placeholder='Password'
                  type={showConfirmPassword ? 'text' : 'password'}
                />
                <PasswordToggleButton
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  tabIndex={-1}
                  type='button'
                >
                  {showConfirmPassword ? 'Hide Password' : 'Show Password'}
                  <span style={{ marginLeft: '8px' }}>
                    {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                  </span>
                </PasswordToggleButton>
              </PasswordFieldWrapper>
            </label>
          </FormGroup>
        </>
      ) : null}
      {showOrgFields ? (
        <FormGroup>
          <label htmlFor='organizationName'>
            <ErrorAndName>
              <Text
                styledTextProps={{ fontSize: theme.textFontSizes[0] }}
              >
                Organization Name
              </Text>
              {errors.organizationName && touched.organizationName && <FieldError className='text-center'>{errors.organizationName}</FieldError>}
            </ErrorAndName>
            <Field
              className='form-control'
              id='organizationName'
              name='organizationName'
              placeholder='Organization Name'
              type='text'
            />
          </label>
        </FormGroup>
      ) : null}
      <FormGroup>
        <FormColumns>
          <ColumnContent>
            <label htmlFor='countryCode'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  Country Code
                </Text>
              </ErrorAndName>
              <Field
                component={CountryCodeSelectField}
                id='countryCode'
                name='countryCode'
              />
            </label>
          </ColumnContent>
          <ColumnContent>
            <label htmlFor='phone'>
              <ErrorAndName>
                <Text
                  styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                >
                  Phone
                </Text>
              </ErrorAndName>
              <Field
                component={PhoneNumberInputField}
                id='phone'
                name='phone'
                placeholder='Phone (optional)'
              />
            </label>
          </ColumnContent>
        </FormColumns>
        {errors.phone && <FieldError className='text-center'>{errors.phone}</FieldError>}
        {errors.countryCode && <FieldError className='text-center'>{errors.countryCode}</FieldError>}
      </FormGroup>
      {showGenderField ? (
        <FormGroup>
          <label htmlFor='gender'>
            <ErrorAndName>
              <Text
                styledTextProps={{ fontSize: theme.textFontSizes[0] }}
              >
                Gender
              </Text>
              {errors.gender && touched.gender && <FieldError className='text-center'>{errors.gender}</FieldError>}
            </ErrorAndName>
            <Select
              defaultValue={genderOptions[values.gender]}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              id='gender'
              isClearable
              isOptionSelected={(option) => values.gender === option.id}
              isSearchable
              onChange={(selection) => {
                if (selection) {
                  setFieldValue('gender', selection.id);
                } else {
                  setFieldValue('gender', 0);
                }
              }}
              options={[{ name: 'Male', id: 1 }, { name: 'Female', id: 2 }, { name: 'Other', id: 3 }]}
              placeholder='Select... (optional)'
            />
          </label>
        </FormGroup>
      ) : null}
    </>
  );
};

CommonFields.propTypes = {
  errors: PropTypes.shape({
    confirmedPassword: PropTypes.string,
    countryCode: PropTypes.string,
    emailAddress: PropTypes.string,
    firstName: PropTypes.string,
    gender: PropTypes.string,
    lastName: PropTypes.string,
    organizationName: PropTypes.string,
    password: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  showGenderField: PropTypes.bool,
  showOrgFields: PropTypes.bool,
  showPasswordFields: PropTypes.bool,
  touched: PropTypes.shape({
    confirmedPassword: PropTypes.bool,
    countryCode: PropTypes.bool,
    emailAddress: PropTypes.bool,
    firstName: PropTypes.bool,
    gender: PropTypes.bool,
    lastName: PropTypes.bool,
    organizationName: PropTypes.bool,
    password: PropTypes.bool,
    phone: PropTypes.bool,
  }).isRequired,
  values: PropTypes.shape({
    confirmedPassword: PropTypes.string,
    countryCode: PropTypes.string,
    emailAddress: PropTypes.string,
    firstName: PropTypes.string,
    gender: PropTypes.number,
    lastName: PropTypes.string,
    organizationName: PropTypes.string,
    password: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
};

CommonFields.defaultProps = {
  showGenderField: true,
  showPasswordFields: true,
  showOrgFields: false,
};

export default CommonFields;
